/**
 * Main layout with navigation header and content area.
 */

import { Activity, FileText, Key, LayoutDashboard, LogOut, ScrollText, Search, Settings } from "lucide-react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth-context.tsx";

const NAV_ITEMS = [
  { path: "/", label: "Dashboard", icon: LayoutDashboard },
  { path: "/certificates", label: "Certificates", icon: Key },
  { path: "/invoices", label: "Invoices", icon: FileText },
  { path: "/entities", label: "Entity Lookup", icon: Search },
  { path: "/logs", label: "Audit Logs", icon: ScrollText },
  { path: "/workflows", label: "Workflows", icon: Activity },
  { path: "/settings", label: "Settings", icon: Settings },
] as const;

export function Layout() {
  const { pathname } = useLocation();
  const { logout } = useAuth();

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      {/* Header */}
      <header className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-8">
              <h1 className="text-lg font-semibold text-gray-900 dark:text-white">KSeF Operations</h1>
              <nav className="hidden md:flex items-center gap-1">
                {NAV_ITEMS.map(({ path, label, icon: Icon }) => {
                  const isActive = path === "/" ? pathname === "/" : pathname.startsWith(path);
                  return (
                    <Link
                      key={path}
                      to={path}
                      className={`flex items-center gap-2 px-3 py-2 text-sm rounded-md transition-colors ${
                        isActive
                          ? "bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                          : "text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      {label}
                    </Link>
                  );
                })}
              </nav>
            </div>
            <button
              type="button"
              onClick={logout}
              className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Outlet />
      </main>
    </div>
  );
}
